import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { ProfileProvider } from "./contexts/ProfileContext";
import { jwtDecode } from "jwt-decode";

import Login from "./components/Login";
import Register from "./components/Register";
import AuthCallback from "./components/AuthCallback";
import CandidateLayout from "./components/candidate/layout/CandidateLayout";
import CorporateLayout from "./components/corporate/layout/CorporateLayout";
import CorporateProfile from "./components/corporate/CorporateProfile";
import CandidateProfileLayout from "./components/candidate/layout/CandidateProfileLayout";
import ScheduleJobInterview from "./components/candidate/ScheduleJobInterview";
import SearchJobs from "./components/candidate/SearchJobs";
import { RolesProvider } from "./contexts/RolesContext";
import CorporateJobPostings from "./components/corporate/CorporateJobPostings";
import CreateJob from "./components/corporate/CreateJob";
import DiscoverCandidates from "./components/corporate/DiscoverCandidates";
import FunctionRoles from "./components/corporate/FunctionRoles";
import RoleCandidates from "./components/corporate/RoleCandidates";
import JobApplications from "./components/corporate/JobApplications";
import { useProfile } from "./contexts/ProfileContext";
import UserSettings from "./components/candidate/UserSettings";
import InterviewDashboard from "./components/candidate/InterviewDashboard";
import SkillMatchingPage from "./components/corporate/SkillMatchingPage";
import Cookies from "js-cookie";
import { useEffect } from "react";
// import theme from "./mui-theme-colors"; // path to the file created earlier

// Candidate Default Redirect Component
const CandidateDefaultRedirect = () => {
  const { profileReviewStatus, loading } = useProfile();

  // If still loading, show loading message
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          width: "100vw",
          fontSize: "16px",
          color: "#666",
          textAlign: "center",
          backgroundColor: "#f9f9f9", // optional, for better contrast
        }}
      >
        Loading your profile status...
      </div>
    );
  }

  // If profile review status is null (no profile exists yet), redirect to Profile
  if (!profileReviewStatus) {
    return <Navigate to="/candidate/profile" replace />;
  }

  // If approved and active, redirect to Search Jobs (only for the default route)
  if (
    profileReviewStatus.review_status === "Approved" &&
    (profileReviewStatus.isActive === 1 ||
      profileReviewStatus.isActive === true)
  ) {
    return <Navigate to="/candidate/interview-dashboard" replace />;
  }

  // For all other cases (Under Review, Rejected, etc.), redirect to Profile

  return <Navigate to="/candidate/profile" replace />;
};

// Create a theme instance
const theme = createTheme({
  palette: {
    primary: {
      main: "#6C5CE7", // Purple shade
    },
    background: {
      default: "#F8F9FA",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontSize: "1rem",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
          },
        },
      },
    },
  },
});

// const theme = createTheme({
//   palette: {
//     primary: {
//       main: "#FF5A63", // EQuAlly Coral - Main accent
//       dark: "#E64850",
//       light: "#FF7A80",
//       contrastText: "#FFFFFF",
//     },
//     secondary: {
//       main: "#9B42E9", // Purple Gradient start
//       dark: "#8135D1",
//       light: "#C044F7", // Purple Gradient end
//       contrastText: "#FFFFFF",
//     },
//     background: {
//       default: "#080B28", // Midnight Navy
//       paper: "#352C59", // Deep Violet for cards/surfaces
//     },
//     text: {
//       primary: "#FFFFFF",
//       secondary: "rgba(255, 255, 255, 0.7)",
//     },
//     info: {
//       main: "#20A7C7", // Aqua Gradient start
//       light: "#2EE5C6", // Aqua Gradient end
//       dark: "#1A8BA8",
//       contrastText: "#FFFFFF",
//     },
//     success: {
//       main: "#2EE5C6",
//       dark: "#26C4AA",
//       light: "#4EEBD4",
//     },
//     divider: "rgba(255, 255, 255, 0.12)",
//   },
//   typography: {
//     fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
//     h1: {
//       fontWeight: 700,
//       fontSize: "3rem",
//       letterSpacing: "-0.02em",
//     },
//     h2: {
//       fontWeight: 700,
//       fontSize: "2.5rem",
//       letterSpacing: "-0.01em",
//     },
//     h3: {
//       fontWeight: 600,
//       fontSize: "2rem",
//     },
//     h4: {
//       fontWeight: 600,
//       fontSize: "1.5rem",
//     },
//     button: {
//       fontWeight: 600,
//       letterSpacing: "0.02em",
//     },
//   },
//   shape: {
//     borderRadius: 12,
//   },
//   components: {
//     MuiButton: {
//       styleOverrides: {
//         root: {
//           borderRadius: "12px",
//           textTransform: "none",
//           fontSize: "1rem",
//           fontWeight: 600,
//           padding: "10px 24px",
//           boxShadow: "none",
//           transition: "all 0.3s ease",
//           "&:hover": {
//             boxShadow: "0 4px 12px rgba(255, 90, 99, 0.3)",
//             transform: "translateY(-2px)",
//           },
//         },
//         contained: {
//           background: "linear-gradient(135deg, #FF5A63 0%, #FF7A80 100%)",
//           "&:hover": {
//             background: "linear-gradient(135deg, #E64850 0%, #FF5A63 100%)",
//           },
//         },
//         outlined: {
//           borderWidth: "2px",
//           "&:hover": {
//             borderWidth: "2px",
//             backgroundColor: "rgba(255, 90, 99, 0.08)",
//           },
//         },
//       },
//     },
//    MuiTextField: {
//   styleOverrides: {
//     root: {
//       "& .MuiOutlinedInput-root": {
//         borderRadius: "12px",
//         backgroundColor: "rgba(255, 255, 255, 0.05)",
//         transition: "all 0.3s ease",
//         "&:hover": {
//           backgroundColor: "rgba(255, 255, 255, 0.08)",
//         },
//         "&.Mui-focused": {
//           backgroundColor: "rgba(255, 255, 255, 0.08)",
//           boxShadow: "0 0 0 3px rgba(255, 90, 99, 0.2)",
//         },
//         "&.Mui-disabled": {
//           backgroundColor: "rgba(255, 255, 255, 0.02)",
//           "& fieldset": {
//             borderColor: "rgba(255, 255, 255, 0.08)",
//           },
//         },
//         "& fieldset": {
//           borderColor: "rgba(255, 255, 255, 0.12)",
//         },
//         "&:hover fieldset": {
//           borderColor: "rgba(255, 255, 255, 0.2)",
//         },
//       },
//       "& .MuiInputBase-input.Mui-disabled": {
//         WebkitTextFillColor: "rgba(255, 255, 255, 0.4)",
//         color: "rgba(255, 255, 255, 0.4)",
//       },
//       "& .MuiInputLabel-root.Mui-disabled": {
//         color: "rgba(255, 255, 255, 0.3)",
//       },
//     },
//   },
// },
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: "16px",
//           backgroundColor: "#352C59",
//           backgroundImage: "none",
//           boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
//           transition: "all 0.3s ease",
//           "&:hover": {
//             boxShadow: "0 12px 48px rgba(155, 66, 233, 0.2)",
//             transform: "translateY(-4px)",
//           },
//         },
//       },
//     },
//     MuiPaper: {
//       styleOverrides: {
//         root: {
//           backgroundImage: "none",
//         },
//         elevation1: {
//           boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
//         },
//       },
//     },
//     MuiChip: {
//       styleOverrides: {
//         root: {
//           borderRadius: "8px",
//           fontWeight: 500,
//         },
//         filled: {
//           background: "linear-gradient(135deg, #9B42E9 0%, #C044F7 100%)",
//         },
//       },
//     },
//     MuiSidebar: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#080B28",
//           backgroundImage: "none",
//           boxShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",
//         },
//       },
//     },
//     MuiAppBar: {
//       styleOverrides: {
//         root: {
//           backgroundColor: "#080B28",
//           backgroundImage: "none",
//           boxShadow: "0 2px 16px rgba(0, 0, 0, 0.3)",
//         },
//       },
//     },
//   },
// });

// Protected Route component
const ProtectedRoute = ({ children }) => {
  // Get token from cookies
  const token = Cookies.get("token");

  if (!token) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const CandidateRoute = ({ children }) => {
  const token = Cookies.get("token");
  const location = useLocation();

  if (!token) {
    const searchParams = new URLSearchParams(location.search);
    const userTypeParam =
      searchParams.get("userType") || searchParams.get("userTYpe");
    return (
      <Navigate
        to={`/login${userTypeParam ? `?userType=${userTypeParam}` : ""}`}
        replace
      />
    );
  }

  try {
    const decoded = jwtDecode(token);
    const userType = decoded?.userType;

    if (userType !== "candidate") {
      return <Navigate to="/corporate/profile" replace />;
    }

    return <CandidateLayout>{children}</CandidateLayout>;
  } catch (error) {
    console.error("Token decode error:", error);
    Cookies.remove("token");
    const searchParams = new URLSearchParams(location.search);
    const userTypeParam =
      searchParams.get("userType") || searchParams.get("userTYpe");
    return (
      <Navigate
        to={`/login${userTypeParam ? `?userType=${userTypeParam}` : ""}`}
        replace
      />
    );
  }
};

// Corporate Route component
const CorporateRoute = ({ children }) => {
  const token = Cookies.get("token");
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const userTypeParam =
    searchParams.get("userType") || searchParams.get("userTYpe");
  const { corporateRequiresProfile, fetchCorporateProfileStatus } =
    useProfile();

  useEffect(() => {
    // Fetch corporate profile status if not already fetched
    if (token && corporateRequiresProfile === null) {
      fetchCorporateProfileStatus();
    }
  }, [token, corporateRequiresProfile, fetchCorporateProfileStatus]);

  if (!token) {
    return (
      <Navigate
        to={`/login${userTypeParam ? `?userType=${userTypeParam}` : ""}`}
        replace
      />
    );
  }

  try {
    const decoded = jwtDecode(token);
    const userType = decoded?.userType;

    if (userType !== "corporate") {
      return <Navigate to="/candidate" replace />;
    }

    // If requiresProfile is true and user is trying to access a route other than profile, redirect to profile
    if (
      corporateRequiresProfile === true &&
      location.pathname !== "/corporate/profile"
    ) {
      return <Navigate to="/corporate/profile" replace />;
    }

    return <CorporateLayout>{children}</CorporateLayout>;
  } catch (error) {
    console.error("Token decode error:", error);
    Cookies.remove("token");
    return (
      <Navigate
        to={`/login${userTypeParam ? `?userType=${userTypeParam}` : ""}`}
        replace
      />
    );
  }
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ProfileProvider>
          <Routes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth-callback" element={<AuthCallback />} />

            {/* Candidate routes */}
            <Route path="/candidate">
              <Route
                index
                element={
                  <CandidateRoute>
                    <CandidateDefaultRedirect />
                  </CandidateRoute>
                }
              />
              <Route
                path="search-jobs"
                element={
                  <CandidateRoute>
                    <SearchJobs />
                  </CandidateRoute>
                }
              />
              <Route
                path="interview-dashboard"
                element={
                  <CandidateRoute>
                    <InterviewDashboard />
                  </CandidateRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <CandidateRoute>
                    <UserSettings />
                  </CandidateRoute>
                }
              />
              <Route
                path="schedule-interview"
                element={
                  <CandidateRoute>
                    <ScheduleJobInterview />
                  </CandidateRoute>
                }
              />
              <Route
                path="profile/*"
                element={
                  <CandidateRoute>
                    <RolesProvider>
                      <CandidateProfileLayout />
                    </RolesProvider>
                  </CandidateRoute>
                }
              />
            </Route>

            {/* Corporate routes */}
            <Route path="/corporate">
              <Route
                path="profile"
                element={
                  <CorporateRoute>
                    <CorporateProfile />
                  </CorporateRoute>
                }
              />
              <Route
                path="job-postings"
                element={
                  <CorporateRoute>
                    <CorporateJobPostings />
                  </CorporateRoute>
                }
              />
              <Route
                path="create-job"
                element={
                  <CorporateRoute>
                    <CreateJob />
                  </CorporateRoute>
                }
              />
              <Route
                path="edit-job/:jobId"
                element={
                  <CorporateRoute>
                    <CreateJob />
                  </CorporateRoute>
                }
              />
              <Route
                path="interviews"
                element={
                  <CorporateRoute>
                    <JobApplications />
                  </CorporateRoute>
                }
              />
              <Route
                path="discover-candidates"
                element={
                  <CorporateRoute>
                    <DiscoverCandidates />
                  </CorporateRoute>
                }
              />
              <Route
                path="discover-candidates/:functionId"
                element={
                  <CorporateRoute>
                    <FunctionRoles />
                  </CorporateRoute>
                }
              />
              <Route
                path="skill-matching/:jobId"
                element={
                  <CorporateRoute>
                    <SkillMatchingPage />
                  </CorporateRoute>
                }
              />

              <Route
                path="discover-candidates/:functionId/roles/:roleIds/candidates/:jobId"
                element={
                  <CorporateRoute>
                    <RoleCandidates />
                  </CorporateRoute>
                }
              />
              <Route
                path="discover-candidates/search"
                element={
                  <CorporateRoute>
                    <RoleCandidates />
                  </CorporateRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <CorporateRoute>
                    <div>Settings Page (Coming Soon)</div>
                  </CorporateRoute>
                }
              />
            </Route>

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
          </Routes>
        </ProfileProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
